import { useEffect, useRef, useState, type FormEvent } from "react";
import type { CommerceProduct as CommerceProductRecord, CommerceStore, CommerceVariant } from "../api";
import { PlatformApiError, type PlatformApiClient } from "../api";
import type { OrganizationContext } from "../types";
import { CommerceBrandingPanel } from "./CommerceBrandingPanel";
import { CommerceProductImagePanel } from "./CommerceProductImagePanel";
import { AdaptiveGrid, Page, PageHeader, Section, SectionHeader, StatusState } from "../adaptive/primitives";

type WorkspaceRoute = { view: "stores" | "catalog"; storeId: string | null };

type ArchiveTarget =
  | { kind: "store"; record: CommerceStore }
  | { kind: "product"; storeId: string; record: CommerceProductRecord }
  | { kind: "variant"; storeId: string; product: CommerceProductRecord; record: CommerceVariant };

/** Prevent an obsolete store request from rendering a different store\u2019s catalog. */
export function isCurrentCommerceCatalogRequest(
  requestedStoreId: string, requestEpoch: number, currentStoreId: string | null, latestEpoch: number,
): boolean {
  return requestedStoreId === currentStoreId && requestEpoch === latestEpoch;
}

export function isCommerceCatalogVisible(loadedStoreId: string | null, selectedStoreId: string | null): boolean {
  return loadedStoreId !== null && loadedStoreId === selectedStoreId;
}

export function canConfirmCommerceItemArchive(
  targetStoreId: string, selectedStoreId: string | null, selectedStoreStatus: string | null,
): boolean {
  return targetStoreId === selectedStoreId && selectedStoreStatus !== null && selectedStoreStatus !== "archived";
}

export function parseCommerceWorkspaceRoute(nestedPath: string): WorkspaceRoute {
  const parts = nestedPath.replace(/^\/+/, "").split("/").filter(Boolean);
  if (parts[0] !== "catalog") return { view: "stores", storeId: null };
  const storeId = parts[1] && /^cd_store_[a-f0-9]{32}$/.test(parts[1]) ? parts[1] : null;
  return { view: "catalog", storeId };
}

function currencyFractionDigits(currency: string): number | null {
  try {
    const digits = new Intl.NumberFormat("es-AR", { style: "currency", currency })
      .resolvedOptions().maximumFractionDigits;
    return typeof digits === "number" ? digits : null;
  } catch {
    return null;
  }
}

export function parseCommercePriceMinor(value: string, currency = "ARS"): number | null {
  const fractionDigits = currencyFractionDigits(currency);
  if (fractionDigits === null) return null;

  const normalized = value.trim().replace(",", ".");
  const match = /^(0|[1-9]\d{0,8})(?:\.(\d{1,8}))?$/.exec(normalized);
  if (!match) return null;

  const whole = match[1]!;
  let fraction = match[2] ?? "";
  if (fraction.length > fractionDigits) {
    const excessDigits = fraction.slice(fractionDigits);
    // Currency changes can leave a zero-padded old value (e.g. 12500.00 → JPY).
    // Ignore excess decimal zeros, but never round a non-zero amount silently.
    if (/[^0]/.test(excessDigits)) return null;
    fraction = fraction.slice(0, fractionDigits);
  }

  const amount = Number(whole) * (10 ** fractionDigits)
    + Number(fraction.padEnd(fractionDigits, "0") || "0");
  return Number.isSafeInteger(amount) && amount >= 0 ? amount : null;
}

function slugify(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 63);
}

function formatMoney(priceMinor: number, currency: string): string {
  const fractionDigits = currencyFractionDigits(currency) ?? 2;
  const amount = priceMinor / (10 ** fractionDigits);
  try {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount);
  } catch {
    return currency + " " + amount.toFixed(fractionDigits);
  }
}

function errorText(error: unknown): string {
  if (error instanceof PlatformApiError) {
    if (error.status === 409 && error.code === "version_conflict") return "Este registro cambió en otra sesión. Actualizá la información e intentá de nuevo.";
    return error.message;
  }
  return error instanceof Error ? error.message : "No se pudo completar la operación.";
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    draft: "Borrador",
    published: "Publicado",
    archived: "Archivado",
    active: "Activo",
  };
  return labels[status] ?? status;
}

function StoreBadge({ status }: { status: string }) {
  return <span className="commerce-status" data-status={status}>{statusLabel(status)}</span>;
}

export function CommerceProduct({
  organization,
  nestedPath,
  api,
  navigate,
  onExpired,
}: {
  organization: OrganizationContext;
  nestedPath: string;
  api: PlatformApiClient;
  navigate: (path: string) => void;
  onExpired: () => void;
}) {
  const organizationId = organization.organization.id;
  const organizationSlug = organization.organization.slug;
  const route = parseCommerceWorkspaceRoute(nestedPath);
  const [stores, setStores] = useState<readonly CommerceStore[]>([]);
  const [storesState, setStoresState] = useState<"loading" | "ready" | "error">("loading");
  const [storesError, setStoresError] = useState("");
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(route.storeId);
  const selectedStoreIdRef = useRef<string | null>(selectedStoreId);
  selectedStoreIdRef.current = selectedStoreId;
  const catalogRequestEpoch = useRef(0);
  const [products, setProducts] = useState<readonly CommerceProductRecord[]>([]);
  const [variantsByProduct, setVariantsByProduct] = useState<Record<string, readonly CommerceVariant[]>>({});
  const [catalogState, setCatalogState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [loadedCatalogStoreId, setLoadedCatalogStoreId] = useState<string | null>(null);
  const [catalogError, setCatalogError] = useState("");
  const [actionError, setActionError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingArchive, setPendingArchive] = useState<ArchiveTarget | null>(null);
  const archiveCancelRef = useRef<HTMLButtonElement>(null);
  const pendingCreateKeys = useRef(new Map<string, string>());
  const [showStoreForm, setShowStoreForm] = useState(false);
  const [storeName, setStoreName] = useState("");
  const [storeSlug, setStoreSlug] = useState("");
  const [storeSlugTouched, setStoreSlugTouched] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [productName, setProductName] = useState("");
  const [productSlug, setProductSlug] = useState("");
  const [productSlugTouched, setProductSlugTouched] = useState(false);
  const [productDescription, setProductDescription] = useState("");
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [editProductName, setEditProductName] = useState("");
  const [editProductSlug, setEditProductSlug] = useState("");
  const [editProductDescription, setEditProductDescription] = useState("");
  const [variantFormProductId, setVariantFormProductId] = useState<string | null>(null);
  const [variantName, setVariantName] = useState("");
  const [variantSku, setVariantSku] = useState("");
  const [variantPrice, setVariantPrice] = useState("");
  const [editingVariantId, setEditingVariantId] = useState<string | null>(null);
  const [editVariantName, setEditVariantName] = useState("");
  const [editVariantSku, setEditVariantSku] = useState("");
  const [editVariantPrice, setEditVariantPrice] = useState("");
  const [editVariantCurrency, setEditVariantCurrency] = useState("ARS");

  function pendingCreateKey(operation: string, payload: unknown): { fingerprint: string; key: string } {
    const fingerprint = operation + ":" + JSON.stringify(payload);
    const existing = pendingCreateKeys.current.get(fingerprint);
    if (existing) return { fingerprint, key: existing };
    const key = "commerce-web-" + globalThis.crypto.randomUUID();
    pendingCreateKeys.current.set(fingerprint, key);
    return { fingerprint, key };
  }

  const selectedStore = stores.find((store) => store.id === selectedStoreId) ?? null;

  useEffect(() => { setPendingArchive(null); }, [organizationId, selectedStoreId]);
  useEffect(() => { if (pendingArchive) archiveCancelRef.current?.focus(); }, [pendingArchive]);

  useEffect(() => {
    let cancelled = false;
    setStoresState("loading");
    setStoresError("");
    api.listCommerceStores(organizationId).then(({ stores: nextStores }) => {
      if (cancelled) return;
      setStores(nextStores);
      setStoresState("ready");
      setSelectedStoreId((current) => {
        if (route.storeId && nextStores.some((store) => store.id === route.storeId)) return route.storeId;
        if (current && nextStores.some((store) => store.id === current)) return current;
        return nextStores[0]?.id ?? null;
      });
    }).catch((cause: unknown) => {
      if (cancelled) return;
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      setStoresState("error");
      setStoresError(errorText(cause));
    });
    return () => { cancelled = true; };
  }, [api, organizationId, route.storeId]);

  useEffect(() => {
    if (route.storeId && stores.some((store) => store.id === route.storeId)) setSelectedStoreId(route.storeId);
  }, [route.storeId, stores]);

  useEffect(() => {
    let cancelled = false;
    const requestEpoch = ++catalogRequestEpoch.current;
    if (storesState !== "ready" || !selectedStore) {
      setProducts([]);
      setVariantsByProduct({});
      setCatalogState("idle");
      setLoadedCatalogStoreId(null);
      setCatalogError("");
      return () => { cancelled = true; };
    }
    setCatalogState("loading");
    setLoadedCatalogStoreId(null);
    setCatalogError("");
    api.listCommerceProducts(organizationId, selectedStore.id).then(async ({ products: nextProducts }) => {
      const pairs = await Promise.all(nextProducts.map(async (product) => {
        const { variants } = await api.listCommerceVariants(organizationId, selectedStore.id, product.id);
        return [product.id, variants] as const;
      }));
      if (cancelled || !isCurrentCommerceCatalogRequest(selectedStore.id, requestEpoch, selectedStoreIdRef.current, catalogRequestEpoch.current)) return;
      setProducts(nextProducts);
      setVariantsByProduct(Object.fromEntries(pairs));
      setLoadedCatalogStoreId(selectedStore.id);
      setCatalogState("ready");
    }).catch((cause: unknown) => {
      if (cancelled || !isCurrentCommerceCatalogRequest(selectedStore.id, requestEpoch, selectedStoreIdRef.current, catalogRequestEpoch.current)) return;
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      setCatalogState("error");
      setCatalogError(errorText(cause));
    });
    return () => { cancelled = true; };
  }, [api, organizationId, selectedStore?.id, storesState]);

  function goToStores() {
    setPendingArchive(null);
    navigate("/o/" + organizationSlug + "/commerce/stores");
  }

  function goToCatalog(storeId: string) {
    setPendingArchive(null);
    selectedStoreIdRef.current = storeId;
    ++catalogRequestEpoch.current;
    setSelectedStoreId(storeId);
    navigate("/o/" + organizationSlug + "/commerce/catalog/" + storeId);
  }

  async function reloadStores() {
    setStoresState("loading");
    setStoresError("");
    try {
      const result = await api.listCommerceStores(organizationId);
      setStores(result.stores);
      setStoresState("ready");
      return result.stores;
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      setStoresState("error");
      setStoresError(errorText(cause));
      throw cause;
    }
  }

  async function reloadCatalog(storeId: string) {
    if (selectedStoreIdRef.current !== storeId) return;
    const requestEpoch = ++catalogRequestEpoch.current;
    setCatalogState("loading");
    setLoadedCatalogStoreId(null);
    setCatalogError("");
    try {
      const result = await api.listCommerceProducts(organizationId, storeId);
      const pairs = await Promise.all(result.products.map(async (product) => {
        const variants = await api.listCommerceVariants(organizationId, storeId, product.id);
        return [product.id, variants.variants] as const;
      }));
      if (!isCurrentCommerceCatalogRequest(storeId, requestEpoch, selectedStoreIdRef.current, catalogRequestEpoch.current)) return;
      setProducts(result.products);
      setVariantsByProduct(Object.fromEntries(pairs));
      setLoadedCatalogStoreId(storeId);
      setCatalogState("ready");
    } catch (cause) {
      if (!isCurrentCommerceCatalogRequest(storeId, requestEpoch, selectedStoreIdRef.current, catalogRequestEpoch.current)) throw cause;
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      setCatalogState("error");
      setCatalogError(errorText(cause));
      throw cause;
    }
  }

  async function submitStore(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setActionError(""); setNotice("");
    try {
      const payload = { name: storeName.trim(), slug: storeSlug.trim() };
      const pendingKey = pendingCreateKey("store", payload);
      const result = await api.createCommerceStore(organizationId, payload, pendingKey.key);
      const next = await reloadStores();
      pendingCreateKeys.current.delete(pendingKey.fingerprint);
      const store = next.find((item) => item.id === result.store.id) ?? result.store;
      setSelectedStoreId(store.id);
      setStoreName(""); setStoreSlug(""); setStoreSlugTouched(false); setShowStoreForm(false);
      setNotice("La tienda quedó creada en borrador. Ya podés cargar productos y variantes.");
      goToCatalog(store.id);
    } catch (cause) {
      setActionError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function submitProduct(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || !selectedStore) return;
    setBusy(true); setActionError(""); setNotice("");
    try {
      const payload = { name: productName.trim(), slug: productSlug.trim(), description: productDescription.trim() || undefined };
      const pendingKey = pendingCreateKey("product:" + selectedStore.id, payload);
      const result = await api.createCommerceProduct(organizationId, selectedStore.id, payload, pendingKey.key);
      await reloadCatalog(selectedStore.id);
      pendingCreateKeys.current.delete(pendingKey.fingerprint);
      setProductName(""); setProductSlug(""); setProductSlugTouched(false); setProductDescription(""); setShowProductForm(false);
      setNotice(result.created ? "Producto creado en borrador. Agregá al menos una variante para poder publicarlo." : "Se recuperó la creación previa del producto.");
    } catch (cause) {
      setActionError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function saveProduct(event: FormEvent<HTMLFormElement>, product: CommerceProductRecord) {
    event.preventDefault();
    if (busy || !selectedStore) return;
    setBusy(true); setActionError(""); setNotice("");
    try {
      await api.updateCommerceProduct(organizationId, selectedStore.id, product.id, {
        name: editProductName.trim(), slug: editProductSlug.trim(), description: editProductDescription.trim() || null,
        expectedVersion: product.version,
      });
      await reloadCatalog(selectedStore.id);
      setEditingProductId(null);
      setNotice("Producto actualizado.");
    } catch (cause) {
      setActionError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function changeProductStatus(product: CommerceProductRecord, status: "published" | "archived") {
    if (busy || !selectedStore) return;
    setBusy(true); setActionError(""); setNotice("");
    try {
      await api.updateCommerceProduct(organizationId, selectedStore.id, product.id, { status, expectedVersion: product.version });
      await reloadCatalog(selectedStore.id);
      setNotice(status === "published" ? "Producto publicado en el catálogo privado de Commerce." : "Producto archivado.");
    } catch (cause) {
      setActionError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function submitVariant(event: FormEvent<HTMLFormElement>, product: CommerceProductRecord) {
    event.preventDefault();
    if (busy || !selectedStore) return;
    const priceMinor = parseCommercePriceMinor(variantPrice);
    if (priceMinor === null) { setActionError("Ingresá un precio válido, con hasta dos decimales."); return; }
    setBusy(true); setActionError(""); setNotice("");
    try {
      const payload = { name: variantName.trim(), sku: variantSku.trim(), priceMinor, currency: "ARS" };
      const pendingKey = pendingCreateKey("variant:" + selectedStore.id + ":" + product.id, payload);
      const result = await api.createCommerceVariant(organizationId, selectedStore.id, product.id, payload, pendingKey.key);
      await reloadCatalog(selectedStore.id);
      pendingCreateKeys.current.delete(pendingKey.fingerprint);
      setVariantName(""); setVariantSku(""); setVariantPrice(""); setVariantFormProductId(null);
      setNotice(result.created ? "Variante creada y activa." : "Se recuperó la creación previa de la variante.");
    } catch (cause) {
      setActionError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function saveVariant(event: FormEvent<HTMLFormElement>, product: CommerceProductRecord, variant: CommerceVariant) {
    event.preventDefault();
    if (busy || !selectedStore) return;
    const priceMinor = parseCommercePriceMinor(editVariantPrice, editVariantCurrency.trim().toUpperCase());
    if (priceMinor === null) { setActionError("Ingresá un importe válido para la precisión de la moneda seleccionada."); return; }
    setBusy(true); setActionError(""); setNotice("");
    try {
      await api.updateCommerceVariant(organizationId, selectedStore.id, product.id, variant.id, {
        name: editVariantName.trim(), sku: editVariantSku.trim(), priceMinor,
        currency: editVariantCurrency.trim().toUpperCase(), expectedVersion: variant.version,
      });
      await reloadCatalog(selectedStore.id);
      setEditingVariantId(null);
      setNotice("Variante actualizada.");
    } catch (cause) {
      setActionError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function archiveVariant(product: CommerceProductRecord, variant: CommerceVariant) {
    if (busy || !selectedStore) return;
    setBusy(true); setActionError(""); setNotice("");
    try {
      await api.updateCommerceVariant(organizationId, selectedStore.id, product.id, variant.id, { status: "archived", expectedVersion: variant.version });
      await reloadCatalog(selectedStore.id);
      setNotice("Variante archivada.");
    } catch (cause) {
      setActionError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function publishStore() {
    if (busy || !selectedStore) return;
    setBusy(true); setActionError(""); setNotice("");
    try {
      await api.updateCommerceStore(organizationId, selectedStore.id, { status: "published", expectedVersion: selectedStore.version });
      await reloadStores();
      setNotice("Tienda publicada. La salida pública todavía requiere completar el renderer y la configuración de dominio.");
    } catch (cause) {
      setActionError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function archiveStore(store: CommerceStore) {
    if (busy) return;
    setBusy(true); setActionError(""); setNotice("");
    try {
      await api.updateCommerceStore(organizationId, store.id, { status: "archived", expectedVersion: store.version });
      const next = await reloadStores();
      if (selectedStoreId === store.id) {
        const nextStore = next.find((item) => item.status !== "archived") ?? null;
        setSelectedStoreId(nextStore?.id ?? null);
        if (route.view === "catalog") {
          if (nextStore) goToCatalog(nextStore.id);
          else goToStores();
        }
      }
      setNotice("Tienda archivada.");
    } catch (cause) {
      setActionError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function confirmArchive() {
    const target = pendingArchive;
    if (!target || busy) return;
    setPendingArchive(null);
    if (target.kind === "store") {
      await archiveStore(target.record);
      return;
    }
    // A confirmation made before switching stores must not mutate the old store.
    if (!canConfirmCommerceItemArchive(target.storeId, selectedStore?.id ?? null, selectedStore?.status ?? null)) return;
    if (target.kind === "product") {
      await changeProductStatus(target.record, "archived");
    } else {
      await archiveVariant(target.product, target.record);
    }
  }

  function startEditProduct(product: CommerceProductRecord) {
    setEditingProductId(product.id);
    setEditProductName(product.name);
    setEditProductSlug(product.slug);
    setEditProductDescription(product.description ?? "");
    setActionError("");
  }

  function startEditVariant(variant: CommerceVariant) {
    setEditingVariantId(variant.id);
    setEditVariantName(variant.name);
    setEditVariantSku(variant.sku);
    const fractionDigits = currencyFractionDigits(variant.currency) ?? 2;
    setEditVariantPrice((variant.priceMinor / (10 ** fractionDigits)).toFixed(fractionDigits));
    setEditVariantCurrency(variant.currency);
    setActionError("");
  }

  const title = route.view === "catalog" ? "Catálogo de productos" : "Tiendas";
  const description = route.view === "catalog"
    ? "Organizá productos, variantes y precios. Los cambios se guardan en el backend de tu organización."
    : "Creá y administrá tiendas desde el espacio privado de ClickDialog.";

  return (
    <Page className="commerce-workspace">
      <PageHeader
        eyebrow="Commerce Storefront · Preview"
        title={title}
        description={description}
        actions={route.view === "catalog" && selectedStore ? (
          <div className="commerce-header-actions">
            {selectedStore.status === "draft" && <button className="primary-button" disabled={busy || !products.some((product) => product.status === "published")} onClick={() => void publishStore()}>Publicar tienda</button>}
            {selectedStore.status !== "archived" && <button disabled={busy} onClick={() => setPendingArchive({ kind: "store", record: selectedStore })}>Archivar tienda</button>}
          </div>
        ) : route.view === "stores" ? <button className="primary-button" onClick={() => setShowStoreForm((open) => !open)}>{showStoreForm ? "Cancelar" : "Nueva tienda"}</button> : undefined}
      />

      {(actionError || storesError || (route.view === "catalog" && catalogError)) && (
        <div className="inline-alert" role="alert">
          {actionError || (route.view === "catalog" ? catalogError : storesError)}
          {storesState === "error" && <button type="button" onClick={() => void reloadStores().catch(() => undefined)}>Reintentar</button>}
          {route.view === "catalog" && catalogState === "error" && selectedStore && <button type="button" onClick={() => void reloadCatalog(selectedStore.id).catch(() => undefined)}>Reintentar catálogo</button>}
        </div>
      )}
      {notice && <div className="success-alert" role="status">{notice}</div>}
      {pendingArchive && (
        <div className="commerce-archive-confirmation" role="group" aria-labelledby="commerce-archive-title" aria-describedby="commerce-archive-detail">
          <div>
            <strong id="commerce-archive-title">¿Confirmás el archivado?</strong>
            <p id="commerce-archive-detail">
              {pendingArchive.kind === "store" ? `La tienda “${pendingArchive.record.name}” quedará en solo lectura.` :
               pendingArchive.kind === "product" ? `El producto “${pendingArchive.record.name}” dejará de estar disponible en el catálogo.` :
               `La variante “${pendingArchive.record.name}” dejará de estar activa.`}
            </p>
          </div>
          <div className="commerce-archive-actions">
            <button ref={archiveCancelRef} type="button" disabled={busy} onClick={() => setPendingArchive(null)}>Cancelar</button>
            <button type="button" className="commerce-danger-button" disabled={busy} onClick={() => void confirmArchive()}>
              Confirmar archivado
            </button>
          </div>
        </div>
      )}

      {route.view === "stores" ? (
        <Section className="commerce-stores-section">
          {showStoreForm && (
            <form className="commerce-form" onSubmit={submitStore}>
              <div className="commerce-form-intro"><span className="commerce-step">01</span><div><h2>Nueva tienda</h2><p>La tienda se crea en borrador y queda aislada dentro de esta organización.</p></div></div>
              <div className="commerce-form-grid">
                <label>Nombre comercial<input required minLength={2} maxLength={160} value={storeName} onChange={(event) => { setStoreName(event.target.value); if (!storeSlugTouched) setStoreSlug(slugify(event.target.value)); }} placeholder="Ej. Casa Nativa" /></label>
                <label>Identificador de tienda<input required pattern="[a-z0-9]+(-[a-z0-9]+)*" maxLength={63} value={storeSlug} onChange={(event) => { setStoreSlugTouched(true); setStoreSlug(slugify(event.target.value)); }} placeholder="casa-nativa" /><small>URL técnica estable; letras minúsculas, números y guiones.</small></label>
              </div>
              <div className="commerce-form-actions"><button type="button" disabled={busy} onClick={() => setShowStoreForm(false)}>Cancelar</button><button type="submit" className="primary-button" disabled={busy}>{busy ? "Guardando…" : "Crear tienda"}</button></div>
            </form>
          )}

          <SectionHeader eyebrow="Espacios comerciales" title="Tus tiendas" description="Cada tienda tiene su propio catálogo, variantes y publicación." />
          {storesState === "loading" ? <StatusState tone="loading" title="Cargando tiendas…" message="Estamos consultando las tiendas de esta organización." /> :
           storesState === "error" ? <StatusState tone="danger" title="No se pudieron cargar las tiendas" message={storesError} action={<button onClick={() => void reloadStores().catch(() => undefined)}>Reintentar</button>} /> :
           stores.length === 0 ? <StatusState title="Todavía no hay tiendas" message="Creá la primera tienda para empezar a cargar tu catálogo." action={<button className="primary-button" onClick={() => setShowStoreForm(true)}>Crear primera tienda</button>} /> :
           <AdaptiveGrid className="commerce-store-grid" compact={1} medium={2} wide={2}>
             {stores.map((store) => <article className="commerce-store-card" key={store.id} data-status={store.status}>
               <div className="commerce-store-symbol" aria-hidden="true">C</div>
               <div className="commerce-store-card-main"><div className="commerce-store-card-title"><h3>{store.name}</h3><StoreBadge status={store.status} /></div><p className="commerce-slug">/{store.slug}</p><p className="commerce-store-meta">Actualizada {new Date(store.updatedAt).toLocaleDateString("es-AR")}</p></div>
               <div className="commerce-card-actions"><button className="primary-button" onClick={() => goToCatalog(store.id)}>Administrar catálogo <span aria-hidden="true">→</span></button>{store.status !== "archived" && <button className="commerce-text-button" disabled={busy} onClick={() => setPendingArchive({ kind: "store", record: store })}>Archivar</button>}</div>
             </article>)}
           </AdaptiveGrid>}
        </Section>
      ) : (
        <Section className="commerce-catalog-section">
          <div className="commerce-catalog-toolbar">
            <div className="commerce-store-select"><label htmlFor="commerce-store-select">Tienda activa</label><select id="commerce-store-select" value={selectedStoreId ?? ""} onChange={(event) => { const id = event.target.value; if (id) goToCatalog(id); }} disabled={storesState !== "ready" || stores.length === 0}><option value="" disabled>Seleccionar tienda</option>{stores.map((store) => <option key={store.id} value={store.id} disabled={store.status === "archived"}>{store.name}{store.status === "archived" ? " · archivada" : ""}</option>)}</select></div>
            <button onClick={goToStores}>Ver tiendas</button>
            {selectedStore && selectedStore.status !== "archived" && <button className="primary-button" onClick={() => setShowProductForm((open) => !open)}>{showProductForm ? "Cancelar" : "Nuevo producto"}</button>}
          </div>

          {selectedStore && <CommerceBrandingPanel key={organizationId + ":" + selectedStore.id}
            organizationId={organizationId} storeId={selectedStore.id} storeStatus={selectedStore.status}
            api={api} onExpired={onExpired} />}

          {!selectedStore && storesState === "ready" && <StatusState title="Elegí o creá una tienda" message="El catálogo pertenece siempre a una tienda concreta." action={<button className="primary-button" onClick={goToStores}>Ir a tiendas</button>} />}
          {selectedStore?.status === "archived" && <StatusState tone="warning" title="Tienda archivada" message="Esta tienda es de solo lectura. Elegí otra tienda para continuar." action={<button onClick={goToStores}>Volver a tiendas</button>} />}
          {selectedStore && selectedStore.status !== "archived" && showProductForm && (
            <form className="commerce-form" onSubmit={submitProduct}>
              <div className="commerce-form-intro"><span className="commerce-step">02</span><div><h2>Nuevo producto</h2><p>Se guarda como borrador. El precio se define en cada variante.</p></div></div>
              <div className="commerce-form-grid">
                <label>Nombre<input required minLength={2} maxLength={160} value={productName} onChange={(event) => { setProductName(event.target.value); if (!productSlugTouched) setProductSlug(slugify(event.target.value)); }} placeholder="Ej. Café de especialidad" /></label>
                <label>Slug<input required pattern="[a-z0-9]+(-[a-z0-9]+)*" maxLength={63} value={productSlug} onChange={(event) => { setProductSlugTouched(true); setProductSlug(slugify(event.target.value)); }} placeholder="cafe-especialidad" /></label>
                <label className="commerce-form-full">Descripción<textarea rows={3} maxLength={4000} value={productDescription} onChange={(event) => setProductDescription(event.target.value)} placeholder="Descripción clara y útil para tus clientes." /></label>
              </div>
              <div className="commerce-form-actions"><button type="button" disabled={busy} onClick={() => setShowProductForm(false)}>Cancelar</button><button className="primary-button" type="submit" disabled={busy}>{busy ? "Guardando…" : "Crear borrador"}</button></div>
            </form>
          )}

          {selectedStore && selectedStore.status !== "archived" && (catalogState === "loading" || (catalogState === "ready" && !isCommerceCatalogVisible(loadedCatalogStoreId, selectedStore.id))) && <StatusState tone="loading" title="Cargando catálogo…" message="Estamos consultando productos y variantes." />}
          {selectedStore && selectedStore.status !== "archived" && catalogState === "error" && <StatusState tone="danger" title="No se pudo cargar el catálogo" message={catalogError} action={<button onClick={() => void reloadCatalog(selectedStore.id).catch(() => undefined)}>Reintentar</button>} />}
          {selectedStore && selectedStore.status !== "archived" && catalogState === "ready" && isCommerceCatalogVisible(loadedCatalogStoreId, selectedStore.id) && (
            <>
              <SectionHeader eyebrow={selectedStore.name} title="Productos" description={products.length + (products.length === 1 ? " producto" : " productos") + " · Agregá variantes con SKU y precio para habilitar la publicación."} />
              {products.length === 0 ? <StatusState title="Catálogo vacío" message="Creá el primer producto y después agregale una variante con precio y SKU." action={<button className="primary-button" onClick={() => setShowProductForm(true)}>Crear primer producto</button>} /> :
              <div className="commerce-product-list">
                {products.map((product) => {
                  const variants = variantsByProduct[product.id] ?? [];
                  const activeVariants = variants.filter((variant) => variant.status === "active");
                  return <article className="commerce-product-card" key={product.id} data-status={product.status}>
                    <div className="commerce-product-card-head">
                      <div><p className="commerce-product-kicker">Producto · {product.slug}</p><h3>{product.name}</h3><p className="commerce-product-description">{product.description || "Sin descripción"}</p></div>
                      <StoreBadge status={product.status} />
                    </div>
                    <div className="commerce-product-card-actions">
                      {product.status === "draft" && <button disabled={busy || activeVariants.length === 0} title={activeVariants.length === 0 ? "Agregá una variante activa antes de publicar." : undefined} onClick={() => void changeProductStatus(product, "published")}>Publicar</button>}
                      {product.status !== "archived" && <button disabled={busy} onClick={() => { startEditProduct(product); }}>Editar</button>}
                      {product.status !== "archived" && <button className="commerce-text-button" disabled={busy} onClick={() => setPendingArchive({ kind: "product", storeId: selectedStore.id, record: product })}>Archivar</button>}
                    </div>
                    {editingProductId === product.id && <form className="commerce-inline-form" onSubmit={(event) => void saveProduct(event, product)}>
                      <div className="commerce-form-grid">
                        <label>Nombre<input required minLength={2} maxLength={160} value={editProductName} onChange={(event) => setEditProductName(event.target.value)} /></label>
                        <label>Slug<input required pattern="[a-z0-9]+(-[a-z0-9]+)*" maxLength={63} value={editProductSlug} onChange={(event) => setEditProductSlug(slugify(event.target.value))} /></label>
                        <label className="commerce-form-full">Descripción<textarea rows={3} maxLength={4000} value={editProductDescription} onChange={(event) => setEditProductDescription(event.target.value)} /></label>
                      </div>
                      <div className="commerce-form-actions"><button type="button" onClick={() => setEditingProductId(null)}>Cancelar</button><button type="submit" className="primary-button" disabled={busy}>{busy ? "Guardando…" : "Guardar cambios"}</button></div>
                    </form>}

                    <CommerceProductImagePanel key={organizationId + ":" + selectedStore.id + ":" + product.id}
                      api={api} organizationId={organizationId} storeId={selectedStore.id} productId={product.id}
                      productName={product.name}
                      editable={selectedStore.status !== "archived" && product.status !== "archived"}
                      onExpired={onExpired} />
                    <div className="commerce-variants">
                      <div className="commerce-variants-heading"><div><strong>Variantes</strong><span>{activeVariants.length} activas</span></div>{product.status !== "archived" && <button className="commerce-text-button" disabled={busy} onClick={() => { setVariantFormProductId(variantFormProductId === product.id ? null : product.id); setVariantName(""); setVariantSku(""); setVariantPrice(""); }}>+ Agregar variante</button>}</div>
                      {variants.length === 0 ? <p className="commerce-variants-empty">Sin variantes. Una variante activa es necesaria para publicar el producto.</p> :
                        <div className="commerce-variant-list">{variants.map((variant) => <div className="commerce-variant-row" key={variant.id} data-status={variant.status}>
                          <div className="commerce-variant-info"><strong>{variant.name}</strong><small>SKU {variant.sku}</small></div>
                          <div className="commerce-variant-price">{formatMoney(variant.priceMinor, variant.currency)}<small><StoreBadge status={variant.status} /></small></div>
                          <div className="commerce-variant-actions">{variant.status === "active" && <><button className="commerce-text-button" disabled={busy} onClick={() => startEditVariant(variant)}>Editar</button><button className="commerce-text-button commerce-danger-button" disabled={busy} onClick={() => setPendingArchive({ kind: "variant", storeId: selectedStore.id, product, record: variant })}>Archivar</button></>}</div>
                          {editingVariantId === variant.id && <form className="commerce-inline-form commerce-variant-edit" onSubmit={(event) => void saveVariant(event, product, variant)}>
                            <div className="commerce-form-grid">
                              <label>Nombre<input required maxLength={160} value={editVariantName} onChange={(event) => setEditVariantName(event.target.value)} /></label>
                              <label>SKU<input required maxLength={80} value={editVariantSku} onChange={(event) => setEditVariantSku(event.target.value.toUpperCase())} /></label>
                              <label>Precio (importe completo)<input required inputMode="decimal" value={editVariantPrice} onChange={(event) => setEditVariantPrice(event.target.value)} placeholder="12500,00" /></label>
                              <label>Moneda (ISO 4217)<input required pattern="[A-Z]{3}" maxLength={3} value={editVariantCurrency} onChange={(event) => setEditVariantCurrency(event.target.value.toUpperCase())} /></label>
                            </div>
                            <div className="commerce-form-actions"><button type="button" onClick={() => setEditingVariantId(null)}>Cancelar</button><button className="primary-button" type="submit" disabled={busy}>{busy ? "Guardando…" : "Guardar variante"}</button></div>
                          </form>}
                        </div>)}</div>}
                      {variantFormProductId === product.id && <form className="commerce-inline-form" onSubmit={(event) => void submitVariant(event, product)}>
                        <div className="commerce-form-grid">
                          <label>Nombre de variante<input required maxLength={160} value={variantName} onChange={(event) => setVariantName(event.target.value)} placeholder="Ej. Bolsa de 250 g" /></label>
                          <label>SKU<input required maxLength={80} value={variantSku} onChange={(event) => setVariantSku(event.target.value.toUpperCase())} placeholder="CAF-250G" /></label>
                          <label>Precio en ARS<input required inputMode="decimal" value={variantPrice} onChange={(event) => setVariantPrice(event.target.value)} placeholder="12500,00" /></label>
                        </div>
                        <p className="commerce-form-help">Ingresá el precio completo; se convertirá a unidades menores enteras antes de enviarse a la API.</p>
                        <div className="commerce-form-actions"><button type="button" onClick={() => setVariantFormProductId(null)}>Cancelar</button><button className="primary-button" type="submit" disabled={busy}>{busy ? "Guardando…" : "Crear variante"}</button></div>
                      </form>}
                    </div>
                  </article>;
                })}
              </div>}
            </>
          )}
        </Section>
      )}
    </Page>
  );
}
